import { CATEGORY_BY_ID } from '../categories';
import type { Registration } from '../registry';

const n = CATEGORY_BY_ID.display.colour;

const COLOR_OPTIONS = [
  ['white', 'SPB_WHITE'],
  ['black', 'SPB_BLACK'],
  ['red', 'SPB_RED'],
  ['green', 'SPB_GREEN'],
  ['blue', 'SPB_BLUE'],
  ['cyan', 'SPB_CYAN'],
  ['magenta', 'SPB_MAGENTA'],
  ['yellow', 'SPB_YELLOW'],
  ['orange', 'SPB_ORANGE'],
];

export const displayRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_oled_setup',
      message0: 'OLED display %1 I2C address %2 (put in "when board starts")',
      args0: [
        { type: 'field_dropdown', name: 'SIZE', options: [['128x64', '128x64'], ['128x32', '128x32'], ['128x128', '128x128'], ['72x40', '72x40'], ['64x48', '64x48']] },
        { type: 'field_number', name: 'ADDR', value: 0x3c, min: 1, max: 119, precision: 1 },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Start an SSD1306 I2C OLED. Uses Wire (SDA 21 / SCL 22 unless an I2C begin block runs first).',
    },
    {
      type: 'esp32_tft_setup',
      message0: 'TFT display %1 CS %2 DC %3 RST %4 rotation %5 (put in "when board starts")',
      args0: [
        {
          type: 'field_dropdown',
          name: 'DRIVER',
          options: [
            ['ST7735 160x128', 'ST7735'],
            ['ST7789 240x240', 'ST7789'],
            ['ST7789 240x320', 'ST7789_320'],
            ['ILI9341 240x320', 'ILI9341'],
          ],
        },
        { type: 'field_number', name: 'CS', value: 5, min: 0, max: 48, precision: 1 },
        { type: 'field_number', name: 'DC', value: 2, min: 0, max: 48, precision: 1 },
        { type: 'field_number', name: 'RST', value: 4, min: -1, max: 48, precision: 1 },
        { type: 'field_dropdown', name: 'ROT', options: [['0', '0'], ['1 (90°)', '1'], ['2 (180°)', '2'], ['3 (270°)', '3']] },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Start a SPI TFT panel (Adafruit driver). Uses default VSPI pins unless an SPI begin block runs first.',
    },
    {
      type: 'esp32_display_fill',
      message0: 'fill display %1',
      args0: [{ type: 'field_dropdown', name: 'COLOR', options: COLOR_OPTIONS }],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Fill the whole screen with a colour.',
    },
    {
      type: 'esp32_display_clear',
      message0: 'clear display',
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Clear the screen to black (call display update after to see it on OLED).',
    },
    {
      type: 'esp32_display_text',
      message0: 'draw text %1 at x %2 y %3 size %4 color %5',
      args0: [
        { type: 'input_value', name: 'TEXT', check: 'String' },
        { type: 'input_value', name: 'X', check: 'Number' },
        { type: 'input_value', name: 'Y', check: 'Number' },
        { type: 'field_number', name: 'SIZE', value: 2, min: 1, max: 10, precision: 1 },
        { type: 'field_dropdown', name: 'COLOR', options: COLOR_OPTIONS },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Draw text at a pixel position. Size 1 = 5x7 px per char, each bigger size multiplies that.',
    },
    {
      type: 'esp32_display_pixel',
      message0: 'draw pixel x %1 y %2 color %3',
      args0: [
        { type: 'input_value', name: 'X', check: 'Number' },
        { type: 'input_value', name: 'Y', check: 'Number' },
        { type: 'field_dropdown', name: 'COLOR', options: COLOR_OPTIONS },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Set a single pixel.',
    },
    {
      type: 'esp32_display_line',
      message0: 'draw line x1 %1 y1 %2 x2 %3 y2 %4 color %5',
      args0: [
        { type: 'input_value', name: 'X1', check: 'Number' },
        { type: 'input_value', name: 'Y1', check: 'Number' },
        { type: 'input_value', name: 'X2', check: 'Number' },
        { type: 'input_value', name: 'Y2', check: 'Number' },
        { type: 'field_dropdown', name: 'COLOR', options: COLOR_OPTIONS },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Draw a straight line between two points.',
    },
    {
      type: 'esp32_display_rect',
      message0: 'draw rect x %1 y %2 w %3 h %4 color %5 filled %6',
      args0: [
        { type: 'input_value', name: 'X', check: 'Number' },
        { type: 'input_value', name: 'Y', check: 'Number' },
        { type: 'input_value', name: 'W', check: 'Number' },
        { type: 'input_value', name: 'H', check: 'Number' },
        { type: 'field_dropdown', name: 'COLOR', options: COLOR_OPTIONS },
        { type: 'field_dropdown', name: 'FILL', options: [['no', 'OUTLINE'], ['yes', 'FILL']] },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Draw a rectangle, optionally filled.',
    },
    {
      type: 'esp32_display_show',
      message0: 'update display (OLED)',
      previousStatement: null,
      nextStatement: null,
      colour: n,
      tooltip: 'Push the drawn content to an OLED panel. TFTs update immediately and do not need this.',
    },
  ],
  metas: [
    {
      type: 'esp32_oled_setup',
      category: 'display',
      label: 'OLED setup (SSD1306)',
      description: 'Start an I2C SSD1306 OLED in one of several sizes (128x64, 128x32, 128x128, 72x40, 64x48).',
      fields: { SIZE: { kind: 'dropdown', options: ['128x64', '128x32', '128x128', '72x40', '64x48'] }, ADDR: { kind: 'number' } },
    },
    {
      type: 'esp32_tft_setup',
      category: 'display',
      label: 'TFT setup (SPI)',
      description: 'Start a SPI TFT panel: ST7735 160x128, ST7789 240x240/240x320 or ILI9341 240x320.',
      fields: {
        DRIVER: { kind: 'dropdown', options: ['ST7735', 'ST7789', 'ST7789_320', 'ILI9341'] },
        CS: { kind: 'number' }, DC: { kind: 'number' }, RST: { kind: 'number' }, ROT: { kind: 'dropdown', options: ['0', '1', '2', '3'] },
      },
    },
    {
      type: 'esp32_display_fill',
      category: 'display',
      label: 'Fill display',
      description: 'Fill the whole screen with a colour.',
      fields: { COLOR: { kind: 'dropdown', options: COLOR_OPTIONS.map(([label]) => label) } as never },
    },
    {
      type: 'esp32_display_clear',
      category: 'display',
      label: 'Clear display',
      description: 'Clear the screen to black.',
      fields: {},
    },
    {
      type: 'esp32_display_text',
      category: 'display',
      label: 'Draw text',
      description: 'Draw text at x/y with a size (1-10) and colour.',
      fields: {
        TEXT: { kind: 'value', types: 'String' },
        X: { kind: 'value', types: 'Number' },
        Y: { kind: 'value', types: 'Number' },
        SIZE: { kind: 'number' },
        COLOR: { kind: 'dropdown', options: COLOR_OPTIONS.map(([label]) => label) } as never,
      },
    },
    {
      type: 'esp32_display_pixel',
      category: 'display',
      label: 'Draw pixel',
      description: 'Set a single pixel.',
      fields: { X: { kind: 'value', types: 'Number' }, Y: { kind: 'value', types: 'Number' }, COLOR: { kind: 'dropdown', options: COLOR_OPTIONS.map(([label]) => label) } as never },
    },
    {
      type: 'esp32_display_line',
      category: 'display',
      label: 'Draw line',
      description: 'Draw a line between two points.',
      fields: {
        X1: { kind: 'value', types: 'Number' }, Y1: { kind: 'value', types: 'Number' },
        X2: { kind: 'value', types: 'Number' }, Y2: { kind: 'value', types: 'Number' },
        COLOR: { kind: 'dropdown', options: COLOR_OPTIONS.map(([label]) => label) } as never,
      },
    },
    {
      type: 'esp32_display_rect',
      category: 'display',
      label: 'Draw rectangle',
      description: 'Draw a rectangle (outline or filled).',
      fields: {
        X: { kind: 'value', types: 'Number' }, Y: { kind: 'value', types: 'Number' },
        W: { kind: 'value', types: 'Number' }, H: { kind: 'value', types: 'Number' },
        COLOR: { kind: 'dropdown', options: COLOR_OPTIONS.map(([label]) => label) } as never,
        FILL: { kind: 'dropdown', options: ['OUTLINE', 'FILL'] },
      },
    },
    {
      type: 'esp32_display_show',
      category: 'display',
      label: 'Update display',
      description: 'Flush the drawing buffer to the OLED (not needed on TFT).',
      fields: {},
    },
  ],
};
